import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BusinessIcon from '@mui/icons-material/Business';
import { useAuth } from '../auth/AuthContext';
import { useManagementAuth } from '../auth/ManagementAuthContext';
import api from '../services/api';

interface StoreOption {
  storeId: string;
  storeName: string;
  companyName?: string;
}

const SelectStorePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, storeIds, switchStore, loading: authLoading } = useAuth();
  const { managementAccount } = useManagementAuth();
  const [storeOptions, setStoreOptions] = useState<StoreOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!currentUser) {
      navigate('/login', { replace: true });
      return;
    }
    // 店舗が1つ以下なら選択不要 → ホームへ
    if (storeIds.length <= 1) {
      navigate('/', { replace: true });
      return;
    }

    const fetchStoreNames = async () => {
      try {
        // managementAccountから企業-店舗の紐付けマップを作成
        const storeToCompanyMap = new Map<string, string>();
        if (managementAccount?.companies && managementAccount?.stores) {
          // managementAccount.storesにはcompany紐付け情報がないため、
          // companies API経由で取得
          for (const company of managementAccount.companies) {
            try {
              const stores = await api.managementCompany.getStores(
                company.id,
                localStorage.getItem('management_token') || ''
              );
              for (const store of stores) {
                storeToCompanyMap.set(store.id, company.name);
              }
            } catch {
              // 個別企業のストア取得失敗は無視
            }
          }
        }

        // 各storeIdの店舗名を取得
        const options: StoreOption[] = await Promise.all(
          storeIds.map(async (id) => {
            try {
              const storeInfo = await api.store.getStoreInfo(id);
              return {
                storeId: id,
                storeName: storeInfo.name || '(店舗名未設定)',
                companyName: storeToCompanyMap.get(id),
              };
            } catch {
              return {
                storeId: id,
                storeName: '(店舗名未設定)',
                companyName: storeToCompanyMap.get(id),
              };
            }
          })
        );

        setStoreOptions(options);
      } catch {
        // フォールバック: IDのみ表示
        setStoreOptions(storeIds.map((id) => ({ storeId: id, storeName: id })));
      } finally {
        setLoading(false);
      }
    };

    fetchStoreNames();
  }, [authLoading, currentUser, storeIds, managementAccount, navigate]);

  const handleSelect = (storeId: string) => {
    switchStore(storeId);
    navigate('/', { replace: true });
  };

  if (authLoading || loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f8f8f9',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: '#f8f8f9',
      }}
    >
      <Card sx={{ maxWidth: 480, width: '100%', boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
              }}
            >
              <img
                src="/recrew_logo_A-3.png"
                alt="Re:crew Logo"
                style={{ height: '40px', width: 'auto' }}
              />
            </Box>
            <Typography variant="subtitle1" color="text.secondary">
              店舗を選択してください
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {storeOptions.map((option) => (
              <Button
                key={option.storeId}
                variant="outlined"
                onClick={() => handleSelect(option.storeId)}
                sx={{
                  py: 2,
                  px: 3,
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  borderColor: '#e0e0e0',
                  borderRadius: '12px',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#e8f4fd',
                    borderColor: '#3E4298',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <StorefrontIcon sx={{ color: '#3E4298', fontSize: 28 }} />
                  <Box>
                    {option.companyName && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#888',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <BusinessIcon sx={{ fontSize: 14, color: '#888' }} />
                        {option.companyName}
                      </Typography>
                    )}
                    <Typography
                      variant="body1"
                      sx={{ color: '#333', fontWeight: 500 }}
                    >
                      {option.storeName}
                    </Typography>
                  </Box>
                </Box>
              </Button>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SelectStorePage;
